import { useState, useEffect } from 'react'
import { Helmet } from 'react-helmet-async'
import { useTranslation } from 'react-i18next'
import { useLocation } from 'react-router-dom'
import { Search } from 'lucide-react'
import Breadcrumb from '@/components/layout/Breadcrumb'
import DonationFilters, { type DonationFilter } from '@/components/donations/DonationFilters'
import CharityCard, { type Charity } from '@/components/donations/CharityCard'
import DonationModal from '@/components/donations/DonationModal'
import Input from '@/components/ui/Input'
import LoadingSpinner from '@/components/ui/LoadingSpinner'
import { donationsApi, type Charity as ApiCharity } from '@/services/donationsApiService'
import { mockCharities } from '@/services/mockDataService'

export default function DonationsPage() {
  const { t } = useTranslation()
  const location = useLocation()
  const [selectedFilter, setSelectedFilter] = useState<DonationFilter>('all')
  const [searchQuery, setSearchQuery] = useState('')
  const [selectedCharity, setSelectedCharity] = useState<Charity | null>(null)
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [suggestedAmount, setSuggestedAmount] = useState<number>()
  const [charities, setCharities] = useState<Charity[]>([])
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  // Fetch charities on mount
  useEffect(() => {
    const fetchCharities = async () => {
      try {
        setIsLoading(true)
        setError(null)
        const category = selectedFilter === 'all' ? undefined : selectedFilter
            const data = await donationsApi.getCharities(category)
            // Map API charities to local format
            const mappedCharities: Charity[] = data
              .filter((c: ApiCharity) => c.category !== 'all') // Filter out 'all' category
              .map((c: ApiCharity) => ({
                id: typeof c.id === 'string' ? c.id : String(c.id),
                name: c.name,
                registry: c.registry,
                address: '', // API doesn't provide this yet
                logo: c.logoUrl || '/logos/default-charity.png',
                category: (c.category === 'zakat' || c.category === 'sadaqah' || c.category === 'orphans') 
                  ? c.category 
                  : 'sadaqah' as 'zakat' | 'sadaqah' | 'orphans',
              }))
        setCharities(mappedCharities)
      } catch (err) {
        console.error('Failed to fetch charities:', err)
        setError(t('donations.errorLoadCharities', 'فشل تحميل قائمة المؤسسات الخيرية'))
        // Fallback to mock data
        setCharities(mockCharities.map((c) => ({
          id: c.id.toString(),
          name: c.name,
          registry: c.registry,
          address: c.address,
          logo: c.logo,
          category: c.category,
        })))
      } finally {
        setIsLoading(false)
      }
    }
    fetchCharities()
  }, [selectedFilter, t])

  // Check if coming from Zakat calculator
  useEffect(() => {
    const state = location.state as { zakatAmount?: number; source?: string } | null
    if (state?.zakatAmount && state?.source === 'zakat-calculator') {
      setSuggestedAmount(state.zakatAmount)
    }
  }, [location.state])

  const filteredCharities = charities.filter((charity) => {
    const matchesFilter = selectedFilter === 'all' || charity.category === selectedFilter
    const matchesSearch =
      charity.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      charity.registry.includes(searchQuery) ||
      (charity.address && charity.address.toLowerCase().includes(searchQuery.toLowerCase()))
    return matchesFilter && matchesSearch
  })

  const handleDonate = (charity: Charity, amount?: number) => {
    setSelectedCharity(charity)
    if (amount) {
      setSuggestedAmount(amount)
    }
    setIsModalOpen(true)
  }

  const [successMessage, setSuccessMessage] = useState<string | null>(null)

  const handleConfirmDonation = async (charity: Charity, amount: number, donorName?: string, donorEmail?: string) => {
    // Donation is already created and completed
    setIsModalOpen(false)
    setSelectedCharity(null)
    setSuggestedAmount(undefined)
    
    // Show success message
    const formattedAmount = amount.toLocaleString(i18n.language === 'ar' ? 'ar-EG' : 'en-US')
    setSuccessMessage(t('donations.successMessage', { 
      charity: charity.name, 
      amount: formattedAmount,
      currency: t('common.currency', 'ج.م'),
      defaultValue: `تم التبرع بنجاح! شكراً لك على تبرعك لمؤسسة ${charity.name} بمبلغ ${formattedAmount} ${t('common.currency', 'ج.م')}`
    }))
    
    // Hide success message after 5 seconds
    setTimeout(() => {
      setSuccessMessage(null)
    }, 5000)
    
    console.log('Donation confirmed:', { charity: charity.name, amount, donorName, donorEmail })
  }

  const breadcrumbItems = [
    { label: t('common.home'), to: '/' },
    { label: t('common.services'), to: '/services/donations' },
    { label: t('donations.title') },
  ]

  return (
    <>
      <Helmet>
        <title>{t('donations.title')} - سكينة</title>
        <meta
          name="description"
          content={t('public.donations.seo.description')}
        />
      </Helmet>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <Breadcrumb items={breadcrumbItems} className="mb-6" />

        <div className="mb-8">
          <h1 className="text-4xl font-bold text-gray-900 mb-4">{t('public.donations.heading')}</h1>
          <p className="text-lg text-gray-600">{t('public.donations.intro')}</p>
        </div>

        {/* Search and Filters */}
        <div className="mb-6 space-y-4">
          <div className="relative max-w-md">
            <Search className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
            <Input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder={t('public.donations.searchPlaceholder')}
              className="pr-10"
            />
          </div>
          <DonationFilters selectedFilter={selectedFilter} onFilterChange={setSelectedFilter} />
        </div>

        {error && (
          <div className="mb-6 bg-red-50 border border-red-200 rounded-lg p-4">
            <p className="text-red-700">{error}</p>
          </div>
        )}

        {successMessage && (
          <div className="mb-6 bg-green-50 border border-green-200 rounded-lg p-4">
            <p className="text-green-700 font-medium">{successMessage}</p>
          </div>
        )}

        {/* Results Count */}
        <div className="mb-4">
          <p className="text-gray-600">{t('public.donations.resultsCount', { count: filteredCharities.length })}</p>
        </div>

        {/* Charity Grid */}
        {isLoading ? (
          <div className="flex items-center justify-center py-12">
            <LoadingSpinner />
          </div>
        ) : filteredCharities.length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {filteredCharities.map((charity) => (
              <CharityCard
                key={charity.id}
                charity={charity}
                onDonate={handleDonate}
              />
            ))}
          </div>
        ) : (
          <div className="text-center py-12">
            <p className="text-gray-600">{t('public.donations.noResults')}</p>
          </div>
        )}

        {/* Donation Modal */}
        <DonationModal
          isOpen={isModalOpen}
          charity={selectedCharity}
          suggestedAmount={suggestedAmount}
          onClose={() => {
            setIsModalOpen(false)
            setSelectedCharity(null)
          }}
          onConfirm={handleConfirmDonation}
        />
      </div>
    </>
  )
}

